<template>
  <v-container class="billing-page py-8">
    <div class="d-flex flex-wrap align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h4 mb-2">
          My subscription
        </h1><p>Manage your plan and AI credits.</p>
      </div>
      <v-btn
        text
        to="/pages/pricing"
      >
        Compare plans
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      outlined
      role="alert"
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="checkoutMessage"
      type="info"
      outlined
    >
      {{ checkoutMessage }}
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      aria-label="Loading subscription"
    ></v-progress-linear>
    <v-btn
      v-if="!summary && !loading"
      outlined
      @click="refresh"
    >
      Try again
    </v-btn>
    <template v-if="summary">
      <v-alert
        type="info"
        outlined
      >
        Stripe test mode is on. Use a test card; no real money is charged.
        {{ summary.rollout.aiCreditsEnforced ? 'AI Text uses 1 credit per action; image restoration uses 5 credits.' : 'AI credit enforcement is not enabled yet.' }}
      </v-alert>
      <v-alert
        v-if="summary.reviewRequired"
        type="warning"
        outlined
      >
        Your billing account requires support review.
      </v-alert>
      <v-alert
        v-if="!summary.checkoutEnabled"
        type="info"
        outlined
      >
        New purchases are not available yet. Existing payments and balances will continue to update.
      </v-alert>
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-5"
            height="100%"
          >
            <h2 class="text-h5 mb-3">
              {{ summary.plan.name }}
            </h2>
            <StorageUsage />
            <p v-if="summary.subscription">
              Payment status: {{ summary.subscription.status }}
            </p>
            <p v-if="summary.subscription && summary.subscription.periodEnd">
              {{ summary.subscription.cancelAt || summary.subscription.cancelAtPeriodEnd ? 'Cancels on' : 'Current period ends' }} {{ date(summary.subscription.cancelAt || summary.subscription.periodEnd) }}
            </p>
            <v-btn
              v-if="summary.canManage"
              outlined
              color="primary"
              :loading="busy === 'portal'"
              :disabled="!!busy"
              @click="openPortal"
            >
              Manage billing
            </v-btn>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-5"
            height="100%"
          >
            <h2 class="text-h5 mb-4">
              AI credits
            </h2>
            <p class="text-h6">
              {{ creditBalance }} credits available
            </p>
            <v-alert
              v-if="creditBalance < 5"
              type="warning"
              outlined
            >
              {{ creditBalance === 0 ? 'No AI credits left.' : 'Not enough credits for image restoration.' }} Add a pack or upgrade below.
            </v-alert>
            <dl class="credit-balances">
              <dt>Included this cycle</dt><dd>{{ summary.credits.included }}</dd>
              <dt>Purchased</dt><dd>{{ summary.credits.purchased }}</dd>
              <dt>Welcome bonus</dt><dd>{{ summary.credits.welcome }}</dd>
              <dt>Referral rewards</dt><dd>{{ summary.credits.referral || 0 }}</dd>
            </dl>
            <p class="text-body-2 mt-4">
              Purchased credits do not expire. Monthly and welcome credits have separate validity periods.
            </p>
            <v-btn
              outlined
              color="primary"
              :loading="busy === 'ai100'"
              :disabled="!!busy || summary.reviewRequired || !summary.checkoutEnabled"
              @click="checkout('ai100')"
            >
              Add 100 credits · $5
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
      <v-card
        v-if="canSubscribe"
        outlined
        class="pa-5 mt-5"
      >
        <h2 class="text-h5 mb-4">
          Choose your plan
        </h2>
        <v-btn
          v-for="plan in paidPlans"
          :key="plan.id"
          class="mr-3 mb-3"
          color="primary"
          :outlined="selectedOffer !== plan.id"
          :loading="busy === plan.id"
          :disabled="!!busy || summary.reviewRequired || !summary.checkoutEnabled"
          @click="checkout(plan.id)"
        >
          {{ plan.name }} · {{ money(plan.amount) }}/month
        </v-btn>
      </v-card>
      <v-card
        v-else-if="summary.rollout.planChangesAvailable && upgradePlans.length"
        outlined
        class="pa-5 mt-5"
      >
        <h2 class="text-h5 mb-3">
          Need more space or AI credits?
        </h2>
        <p>Stripe shows the price and any prorated charge or credit before you confirm. Additional AI credits for this cycle are prorated too.</p>
        <div v-for="plan in upgradePlans" :key="plan.id" class="mb-5">
          <p class="mb-2"><strong>{{ plan.name }}</strong> · {{ money(plan.amount, plan.currency) }}/month · {{ storage(plan.storageBytes) }} · {{ plan.credits }} monthly AI credits</p>
          <v-btn
            color="primary"
            :loading="busy === `upgrade-${plan.id}`"
            :disabled="!!busy || summary.reviewRequired || !summary.checkoutEnabled"
            @click="upgrade(plan.id)"
          >
            Upgrade to {{ plan.name }}
          </v-btn>
        </div>
      </v-card>
      <v-card
        v-if="summary.pendingCheckout"
        outlined
        class="pa-5 mt-5"
      >
        <p>You have a pending subscription checkout. Resume it at its original price, or cancel it before selecting another plan.</p>
        <v-btn
          v-if="summary.pendingOfferId"
          class="mr-3 mb-3"
          color="primary"
          :disabled="!!busy || !summary.checkoutEnabled"
          :loading="busy === summary.pendingOfferId"
          @click="checkout(summary.pendingOfferId)"
        >
          Resume {{ offerName(summary.pendingOfferId) }} checkout
        </v-btn>
        <v-btn
          outlined
          :disabled="!!busy"
          :loading="busy === 'cancel'"
          @click="cancelCheckout"
        >
          Cancel pending checkout
        </v-btn>
      </v-card>
      <v-card
        outlined
        class="pa-5 mt-5"
      >
        <h2 class="text-h5 mb-4">
          Subscription invoices
        </h2>
        <p v-if="historyError">
          {{ historyError }}
        </p>
        <p v-else-if="!invoices.length">
          No subscription invoices yet. One-time purchases appear in your Stripe receipts.
        </p>
        <v-simple-table v-else>
          <thead>
            <tr>
              <th scope="col">
                Date
              </th><th scope="col">
                Amount paid
              </th><th scope="col">
                Status
              </th><th scope="col">
                Invoice
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invoice in invoices"
              :key="invoice.id"
            >
              <td>{{ date(invoice.created) }}</td><td>{{ money(invoice.amountPaid, invoice.currency) }}</td><td>{{ invoice.status }}</td>
              <td>
                <a
                  v-if="invoiceUrl(invoice.url)"
                  :href="invoiceUrl(invoice.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                >View invoice</a>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
      <v-btn
        class="mt-5"
        text
        :loading="loading"
        @click="refresh"
      >
        Refresh billing status
      </v-btn>
      <v-card
        outlined
        class="pa-5 mt-5"
      >
        <h2 class="text-h5 mb-4">
          Recent purchases
        </h2>
        <p v-if="!orders.length">
          No purchases yet.
        </p>
        <v-simple-table v-else>
          <thead>
            <tr>
              <th scope="col">
                Date
              </th><th scope="col">
                Offer
              </th><th scope="col">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="order in orders"
              :key="order.id"
            >
              <td>{{ date(order.created) }}</td><td>{{ offerName(order.offerId) }}</td><td>{{ order.status }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
      <v-card
        outlined
        class="pa-5 mt-5"
      >
        <h2 class="text-h5 mb-4">
          Recent AI credit usage
        </h2>
        <p v-if="!usage.length">
          No AI credit usage yet.
        </p>
        <v-simple-table v-else>
          <thead>
            <tr>
              <th scope="col">
                Date
              </th><th scope="col">
                Action
              </th><th scope="col">
                Credits
              </th><th scope="col">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in usage"
              :key="entry.id"
            >
              <td>{{ date(entry.created) }}</td><td>{{ entry.operation === 'animate' ? 'Photo animation' : entry.operation }}</td><td>{{ entry.credits }}</td><td>{{ usageStatus(entry.status) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import billing, { billingError, newRequestKey, openStripePage } from '@/services/billing'
import StorageUsage from '@/components/StorageUsage.vue'

export default {
  name: 'Billing',
  components: { StorageUsage },
  data: () => ({
    summary: null, invoices: [], orders: [], usage: [], loading: false, busy: '', error: '', historyError: '', requestKeys: {}, pollTimer: null, polls: 0,
  }),
  computed: {
    upgradePlans() { return (this.summary && this.summary.upgradePlans) || [] },
    creditBalance() { return this.summary ? Object.values(this.summary.credits).reduce((sum, value) => sum + value, 0) : 0 },
    selectedOffer() { return this.$route.query.offer || 'personal' },
    paidPlans() { return this.summary ? this.summary.plans.filter(plan => plan.amount > 0) : [] },
    canSubscribe() {
      return !this.summary.subscription || ['canceled', 'incomplete_expired'].includes(this.summary.subscription.status)
    },
    checkoutMessage() {
      if (this.$route.query.checkout === 'success') return 'Checkout was submitted. Payment confirmation may take a little time. Your confirmed balance and plan are shown below.'
      if (this.$route.query.checkout === 'cancelled') return 'Checkout was closed. You can resume it or cancel the pending checkout below.'

      return ''
    },
  },
  mounted() {
    this.refresh()
    window.addEventListener('billing:changed', this.refresh)
    window.addEventListener('storage', this.onStorage)
    window.addEventListener('focus', this.refresh)
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer)
    window.removeEventListener('billing:changed', this.refresh)
    window.removeEventListener('storage', this.onStorage)
    window.removeEventListener('focus', this.refresh)
  },
  methods: {
    onStorage(event) { if (event.key === 'billing-updated') this.refresh() },
    offerName(id) {
      const offer = [...this.summary.plans, this.summary.aiPack].find(item => item.id === id)

      if (offer) return offer.name

      return id === 'plus' ? 'Plus (legacy)' : id
    },
    usageStatus(status) { return { reserved: 'In progress', consumed: 'Used', released: 'Returned' }[status] || status },
    money(amount, currency = 'USD') { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100) },
    storage(bytes) { return bytes < 1000000000 ? `${bytes / 1000000} MB` : `${bytes / 1000000000} GB` },
    date(seconds) { return new Date(seconds * 1000).toLocaleDateString('en-US') },
    invoiceUrl(value) {
      try {
        const url = new URL(value)

        return url.protocol === 'https:' && url.hostname === 'invoice.stripe.com' ? url.href : null
      } catch (_) { return null }
    },
    async refresh() {
      if (this.loading) return
      clearTimeout(this.pollTimer)
      this.loading = true
      this.error = ''
      try {
        this.summary = await billing.summary()
        this.historyError = ''
        try {
          const history = await billing.history()
          this.invoices = history.invoices
          this.orders = history.orders || []
          this.usage = history.usage || []
        } catch (error) { this.historyError = billingError(error) }
      } catch (error) { this.error = billingError(error) } finally {
        this.loading = false
        if (this.$route.query.checkout === 'success' && this.polls < 6) {
          this.polls += 1
          this.pollTimer = setTimeout(() => this.refresh(), 10000)
        }
      }
    },
    async checkout(offerId) {
      this.busy = offerId
      this.error = ''
      if (!this.requestKeys[offerId]) this.$set(this.requestKeys, offerId, newRequestKey())
      try {
        const result = await billing.checkout(offerId, this.requestKeys[offerId])
        openStripePage(result.url)
      } catch (error) {
        this.error = billingError(error)
        const code = error.response && error.response.data && error.response.data.error
        if (['CHECKOUT_CONFIGURATION_REQUIRED', 'ORDER_FINISHED'].includes(code)) this.$delete(this.requestKeys, offerId)
      } finally { this.busy = '' }
    },
    async openPortal() {
      this.busy = 'portal'
      this.error = ''
      try { openStripePage((await billing.portal()).url, 'portal') } catch (error) { this.error = billingError(error) } finally { this.busy = '' }
    },
    async upgrade(offerId) {
      this.busy = `upgrade-${offerId}`; this.error = ''
      try { openStripePage((await billing.upgrade(offerId)).url, 'portal') } catch (error) { this.error = billingError(error) } finally { this.busy = '' }
    },
    async cancelCheckout() {
      this.busy = 'cancel'
      this.error = ''
      try {
        await billing.cancelCheckout()
        this.requestKeys = {}
        await this.refresh()
      } catch (error) { this.error = billingError(error) } finally { this.busy = '' }
    },
  },
}
</script>

<style scoped>
.billing-page { max-width: 1080px; }
.credit-balances { display: grid; grid-template-columns: 1fr auto; gap: 12px; }
.credit-balances dd { margin: 0; font-weight: 600; }
</style>
